import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { JobCountSchema, LocationRequestSchema, LocationResponseSchema } from './types';
import type { LocationRequestType } from './types';
import type { JobCountType } from './types';
import type { LocationResponseType } from './types';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { type QueriesType, QueriesTypeSchema } from '../telematics/types';
import {
    AutoSuggesterResponseSchema,
    type AutoSuggesterResponseType,
} from '../telematics/components/FilterByParameters/featureAutoSuggester';
import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
import { ApiInterface } from '../../utilities/ApiInterface';

export const ModifyValuesSchema = z.record(z.string(), z.any());
export type ModifyValuesType = z.infer<typeof ModifyValuesSchema>;

export const ModifyRequestSchema = z.object({
    query: QueriesTypeSchema,
    values: ModifyValuesSchema,
});
export type ModifyRequestType = z.infer<typeof ModifyRequestSchema>;

export const ModifyResponseSchema = z.object({
    modified: z.number(),
});
export type ModifyResponseType = z.infer<typeof ModifyResponseSchema>;

export const queryAdhoc = {
    // process_status: { $in: [0,1]},
};

export const queryOverdue = {
    // process_status: { $in: [0,1]},
    target_date: { $lt: FriendlyDateGenerator.getTodayYYYYMMDD() },
};

export const getQueryAdhoc = (selectedDay: string, planning_started: string) => {
    return {
        created: { $gt: planning_started },
        target_date: selectedDay,
    };
};

export const JobHistoryEventResponseSchema = z.object({
    event_id: z.number(),
    event_type: z.string(),
    event_description: z.string().catch(''),
    event_time: z.string(),
    user_id: z.number().nullable().catch(null),
    user_email: z.string().nullable().catch(null),
    diff_data: z.any(),
    extra_data: z.any(),
});
export type JobHistoryEventResponseType = z.infer<typeof JobHistoryEventResponseSchema>;

export class LocationApi extends ApiInterface {
    private static _endpoint: string = 'location';

    public static async getAll(api: Api, f: ApiFilterBuilder): Promise<LocationResponseType[]> {
        return this._getAll(api, f, this._endpoint, LocationResponseSchema);
    }

    public static async getWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<LocationResponseType[]> {
        return api.getData<QueriesType, LocationResponseType[]>(
            'post',
            '/location/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            z.array(LocationResponseSchema)
        );
    }

    public static async getCountWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobCountType> {
        return api.getData<QueriesType, JobCountType>(
            'post',
            '/location/count/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            JobCountSchema
        );
    }

    public static async modify(
        api: Api,
        query: QueriesType,
        values: ModifyValuesType
    ): Promise<ModifyResponseType> {
        return api.getData<ModifyRequestType, ModifyResponseType>(
            'post',
            '/location/modify',
            { query, values },
            ModifyRequestSchema,
            ModifyResponseSchema
        );
    }

    public static async updateTargetDate(
        api: Api,
        id: number,
        targetDate: string
    ): Promise<LocationResponseType> {
        return api.patch<LocationResponseType>(
            '/location/' + id,
            'replace',
            '/target_date',
            targetDate,
            LocationResponseSchema
        );
    }

    // public static async getJobsCount(api: Api, filter: ApiFilterBuilder): Promise<JobCountType> {
    //     return api.getData<undefined, JobCountType>(
    //         'get',
    //         '/job/count' + filter.getUri(),
    //         undefined,
    //         z.undefined(),
    //         JobCountSchema
    //     );
    // }

    public static async getOne(api: Api, jobId: number): Promise<LocationResponseType> {
        return api.getData<undefined, LocationResponseType>(
            'get',
            `/location/${jobId}`,
            undefined,
            z.undefined(),
            LocationResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        jobId: number,
        job: LocationRequestType
    ): Promise<LocationResponseType> {
        return api.getData<LocationRequestType, LocationResponseType>(
            'put',
            `/location/${jobId}`,
            job,
            LocationRequestSchema,
            LocationResponseSchema
        );
    }

    public static async geocode(api: Api, jobId: number): Promise<LocationResponseType> {
        return api.getData<{}, LocationResponseType>(
            'post',
            `/location/${jobId}/geocode`,
            {},
            z.object({}),
            LocationResponseSchema
        );
    }

    public static async getByRouteId(api: Api, routeId: number): Promise<LocationResponseType[]> {
        return api.getData<undefined, LocationResponseType[]>(
            'get',
            `/location/${routeId}/jobs`,
            undefined,
            z.undefined(),
            z.array(LocationResponseSchema)
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder
    ): Promise<LocationResponseType[]> {
        return api.getData<undefined, LocationResponseType[]>(
            'get',
            `/locations` + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(LocationResponseSchema)
        );
    }

    public static async removeAll(api: Api) {
        return this.removeWithQuery(api, { $and: [{}, { id: { $nin: [] } }] });
    }

    public static async removeOne(api: Api, id: number) {
        return api.delete(`/location/${id}`);
    }

    public static async removeWithQuery(api: Api, query: QueriesType) {
        return api.getData<QueriesType, { removed: number }>(
            'post',
            `/location/remove`,
            query,
            QueriesTypeSchema,
            z.object({ removed: z.number() })
        );
    }

    public static async getProcessStatus(api: Api, id: number) {
        // GET https://lastmile-test.automapa.pl/api/job/1138?projection=process_status
        return api.getData<undefined, { process_status: number }>(
            'get',
            `/location/${id}?projection=process_status`,
            undefined,
            z.undefined(),
            z.object({ process_status: z.number().catch(-1) })
        );
    }

    //     //         fetch('./api/jobs/suggest?' + featureAutoSuggester.convertRequestToURLSearchParams(request), {
    //     //             method: 'GET',
    public static async getSuggestions(api: Api, uri: string): Promise<AutoSuggesterResponseType> {
        return api.getData<undefined, AutoSuggesterResponseType>(
            'get',
            '/location/suggest?' + uri,
            undefined,
            z.any(),
            AutoSuggesterResponseSchema
        );
    }

    public static async createOne(
        api: Api,
        job: LocationRequestType
    ): Promise<LocationResponseType> {
        return api.getData<LocationRequestType, LocationResponseType>(
            'post',
            '/location',
            job,
            LocationRequestSchema,
            LocationResponseSchema
        );
    }

    public static async setCompleted(api: Api, id: number): Promise<LocationResponseType> {
        let dateYYYYMMDD_HHMMSS = new Date().toISOString().replace('T', ' ').replace('Z', '');
        // remove last 3 digits of seconds 2024-02-28 13:33:13.535
        dateYYYYMMDD_HHMMSS = dateYYYYMMDD_HHMMSS.substring(0, dateYYYYMMDD_HHMMSS.length - 4);
        await Promise.all([
            // api.patch<JobResponseType>('/job/'+id, 'replace', '/process_status', 4, JobResponseSchema),
            api.patch<LocationResponseType>(
                '/location/' + id,
                'replace',
                '/completed',
                dateYYYYMMDD_HHMMSS,
                LocationResponseSchema
            ),
        ]);
        return this.getOne(api, id);
    }

    public static async getCountOverdue(api: Api): Promise<JobCountType> {
        return this.getCountWithQueries(api, queryOverdue, new ApiFilterBuilder(1));
    }

    public static async getOverdue(api: Api): Promise<LocationResponseType[]> {
        return this.getWithQueries(api, queryOverdue, new ApiFilterBuilder(1000));
    }

    public static async revgeo(api: Api, x: number, y: number): Promise<any> {
        return api.getData<undefined, any>(
            'get',
            '/location/revgeo?x=' + x + '&y=' + y,
            undefined,
            z.any(),
            z.any()
        );
    }

    // https://er.automapa.pl/issues/40585
    // POST https://<host>/api/jobs/1578473/history/ {}
    public static async getHistory(api: Api, jobId: number) {
        return api.getData<any, JobHistoryEventResponseType[]>(
            'post',
            `/location/${jobId}/history`,
            {},
            z.object({}),
            z.array(JobHistoryEventResponseSchema)
        );
    }
    /*
    POST http://etoll.mdymowski.indigo/api/location/get_location_types
        Accept: application/json, text/plain,
    Token: 572974bb0b2976e7813f22e3da14ca57

    {}
    */

    public static async getLocationTypes(api: Api): Promise<string[]> {
        return api.getData<any, string[]>(
            'post',
            '/location/get_location_types',
            {},
            z.object({}),
            z.array(z.string())
        );
    }
}
